<script setup lang="ts">
import type { CTItem } from '@/types/vegco/ctitem'
import type { MDBVegcoBucket } from '@/types/vegco/bucket'
import { notifyError } from '@/utils/helpers'
import { getFournisseurLettresDappel } from '@/utils/helpers-vegco'

definePage({
    meta: {
        id: 'vgco.quarantaines',
        title: 'Quarantaines',
        icon: 'mdi-package-variant-closed',
        drawerIndex: 6,
        requiresAuth: true,
        requiresSiteAccess: 'vegco'
    },
})

const CODE_PRODUIT = 'LRC7X6VC-P'
const CULTURE = 'Laitue Romaine'

declare interface VgcoRequete {
    id: string
    demandéPar: string
    dateCréé: string
    type: string
    paletteId: string
}

const theme = useTheme()
const { smAndDown } = useDisplay()
const isLoading = ref(false)
const buckets = ref<MDBVegcoBucket[]>([])
const requetes = ref<VgcoRequete[]>([])

const pendingIds = computed(() => new Set(requetes.value.map(r => r.paletteId)))

declare interface RecolteGroup {
    dateRécolte: string
    items: CTItem[]
    totalQuantité: number
}

declare interface SemiGroup {
    semiId: string
    récoltes: RecolteGroup[]
    totalQuantité: number
    quarantaineCount: number
}

declare interface FournisseurGroup {
    fournisseurId: string
    semis: SemiGroup[]
    totalQuantité: number
    quarantaineCount: number
}

const allItems = computed(() => buckets.value.flatMap(b => b.items))

const grouped = computed((): FournisseurGroup[] => {
    const byFournisseur = new Map<string, Map<string, Map<string, CTItem[]>>>()

    for (const item of allItems.value) {
        const dateKey =
            item.dateRécolte && new Date(item.dateRécolte).getFullYear() > 2000
                ? new Date(item.dateRécolte).toLocaleDateString('fr-CA')
                : '—'

        if (!byFournisseur.has(item.fournisseurId))
            byFournisseur.set(item.fournisseurId, new Map())

        const bySemi = byFournisseur.get(item.fournisseurId)!

        if (!bySemi.has(item.semiId))
            bySemi.set(item.semiId, new Map())

        const byDate = bySemi.get(item.semiId)!

        if (!byDate.has(dateKey))
            byDate.set(dateKey, [])

        byDate.get(dateKey)!.push(item)
    }

    const result: FournisseurGroup[] = []
    for (const [fournisseurId, bySemi] of byFournisseur) {
        const semis: SemiGroup[] = []
        let fournisseurTotal = 0
        let fournisseurQuarantaine = 0

        for (const [semiId, byDate] of bySemi) {
            const récoltes: RecolteGroup[] = []
            let semiTotal = 0
            let semiQuarantaine = 0

            for (const [dateRécolte, items] of byDate) {
                const qtéRécolte = items.reduce((s, i) => s + i.quantité, 0)
                récoltes.push({ dateRécolte, items, totalQuantité: qtéRécolte })
                semiTotal += qtéRécolte
                semiQuarantaine += items.filter(i => i.état === 'Q').length
            }

            semis.push({ semiId, récoltes, totalQuantité: semiTotal, quarantaineCount: semiQuarantaine })
            fournisseurTotal += semiTotal
            fournisseurQuarantaine += semiQuarantaine
        }

        result.push({ fournisseurId, semis, totalQuantité: fournisseurTotal, quarantaineCount: fournisseurQuarantaine })
    }

    return result
})

const totalItems = computed(() => allItems.value.length)
const quarantaineCount = computed(() => allItems.value.filter(i => i.état === 'Q').length)

async function getData() {
    if (isLoading.value) return
    try {
        isLoading.value = true
        const [inventaireRes, requetesRes] = await Promise.all([
            apiProphet.get('vgco/buckets/inventaire', { params: { culture: CULTURE, codeProduit: CODE_PRODUIT } }),
            apiProphet.get('vgco/requetes'),
        ])
        buckets.value = inventaireRes.data as MDBVegcoBucket[]
        requetes.value = requetesRes.data as VgcoRequete[]
    } catch (e) {
        notifyError(e)
    } finally {
        isLoading.value = false
    }
}

async function refreshRequetes() {
    try {
        const res = await apiProphet.get('vgco/requetes')
        requetes.value = res.data as VgcoRequete[]
    } catch {
        // silently ignore poll errors
    }
}

function formatÉtat(état: string) {
    if (état === 'Q') return 'Quarantaine'
    if (état === 'V') return 'Valide'
    return état
}

function étatColor(état: string) {
    return état === 'Q' ? 'warning' : 'success'
}

function étatIcon(état: string) {
    return état === 'Q' ? 'mdi-lock-outline' : 'mdi-check-circle-outline'
}

function formatRéception(date: string) {
    return new Date(date).toLocaleString('fr-CA', { dateStyle: 'short', timeStyle: 'short' })
}

onMounted(getData)

let pollInterval: ReturnType<typeof setInterval> | null = null
onMounted(() => { pollInterval = setInterval(refreshRequetes, 10_000) })
onUnmounted(() => { if (pollInterval) clearInterval(pollInterval) })

function palettesQ(items: CTItem[]) {
    return items.filter(i => i.état === 'Q').map(i => i.paletteId)
}

function palettesV(items: CTItem[]) {
    return items.filter(i => i.état !== 'Q').map(i => i.paletteId)
}

function applyÉtatLocal(paletteIds: string[], nouvelÉtat: string) {
    const idSet = new Set(paletteIds)
    for (const bucket of buckets.value)
        for (const item of bucket.items)
            if (idSet.has(item.paletteId))
                item.état = nouvelÉtat
}

const pendingOriginalÉtat = new Map<string, string>()

function addLocalPending(paletteIds: string[], type: string) {
    const fakes: VgcoRequete[] = paletteIds.map(id => ({
        id: `local-${id}`,
        demandéPar: '',
        dateCréé: new Date().toISOString(),
        type,
        paletteId: id,
    }))
    requetes.value = [
        ...requetes.value.filter(r => !paletteIds.includes(r.paletteId)),
        ...fakes,
    ]
}

function removeLocalPending(paletteIds: string[]) {
    const idSet = new Set(paletteIds)
    requetes.value = requetes.value.filter(
        r => !(r.id.startsWith('local-') && idSet.has(r.paletteId))
    )
}

async function relacherPalettes(paletteIds: string[]) {
    if (!paletteIds.length) return
    for (const id of paletteIds) {
        const item = allItems.value.find(i => i.paletteId === id)
        if (item) pendingOriginalÉtat.set(id, item.état)
    }
    applyÉtatLocal(paletteIds, 'V')
    addLocalPending(paletteIds, 'relache')
    try {
        await apiProphet.post('vgco/requetes/relachequarantaine', paletteIds)
        removeLocalPending(paletteIds)
        await refreshRequetes()
    } catch (e) {
        removeLocalPending(paletteIds)
        for (const id of paletteIds) {
            const orig = pendingOriginalÉtat.get(id)
            if (orig !== undefined) applyÉtatLocal([id], orig)
            pendingOriginalÉtat.delete(id)
        }
        notifyError(e)
    }
}

async function mettrEnQuarantaine(paletteIds: string[]) {
    if (!paletteIds.length) return
    for (const id of paletteIds) {
        const item = allItems.value.find(i => i.paletteId === id)
        if (item) pendingOriginalÉtat.set(id, item.état)
    }
    applyÉtatLocal(paletteIds, 'Q')
    addLocalPending(paletteIds, 'quarantaine')
    try {
        await apiProphet.post('vgco/requetes/remettrequarantaine', paletteIds)
        removeLocalPending(paletteIds)
        await refreshRequetes()
    } catch (e) {
        removeLocalPending(paletteIds)
        for (const id of paletteIds) {
            const orig = pendingOriginalÉtat.get(id)
            if (orig !== undefined) applyÉtatLocal([id], orig)
            pendingOriginalÉtat.delete(id)
        }
        notifyError(e)
    }
}

const confirmDialog = ref(false)
const confirmRequeteId = ref<string | null>(null)
const confirmPaletteId = ref<string | null>(null)

function promptAnnulerRequete(paletteId: string) {
    const requete = requetes.value.find(r => r.paletteId === paletteId)
    if (!requete) return
    confirmRequeteId.value = requete.id
    confirmPaletteId.value = paletteId
    confirmDialog.value = true
}

async function confirmerAnnulation() {
    if (!confirmRequeteId.value || !confirmPaletteId.value) return
    const id = confirmRequeteId.value
    const paletteId = confirmPaletteId.value
    confirmDialog.value = false
    confirmRequeteId.value = null
    confirmPaletteId.value = null
    requetes.value = requetes.value.filter(r => r.id !== id)
    const originalÉtat = pendingOriginalÉtat.get(paletteId)
    if (originalÉtat !== undefined) {
        applyÉtatLocal([paletteId], originalÉtat)
        pendingOriginalÉtat.delete(paletteId)
    }
    if (id.startsWith('local-')) return
    try {
        await apiProphet.delete(`vgco/requetes/${id}`)
    } catch (e) {
        await refreshRequetes()
        notifyError(e)
    }
}
</script>

<template>
    <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading"
        style="height: 85vh; display: flex; flex-direction: column;">

        <!-- Page header -->
        <div class="page-header px-5 pt-5 pb-3">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">

                <!-- Left: icon + title/subtitle -->
                <div class="d-flex align-center" style="gap: 12px;">
                    <v-avatar color="warning" variant="tonal" size="44" rounded="lg">
                        <v-icon icon="mdi-package-variant-closed" size="26" />
                    </v-avatar>
                    <div>
                        <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Quarantaines</div>
                        <div class="text-caption text-medium-emphasis">{{ CODE_PRODUIT }} · {{ CULTURE }}</div>
                    </div>
                    <v-divider vertical class="mx-2" style="height: 32px; align-self: center;" />
                    <v-chip v-if="!isLoading && totalItems > 0" color="primary" variant="tonal" size="small"
                        prepend-icon="mdi-layers-outline">
                        {{ totalItems }} palettes
                    </v-chip>
                    <v-chip v-if="!isLoading && quarantaineCount > 0" color="warning" variant="tonal" size="small"
                        :prepend-icon="smAndDown ? undefined : 'mdi-lock-outline'">
                        {{ quarantaineCount }}{{ smAndDown ? ' Q' : ' en quarantaine' }}
                    </v-chip>
                </div>

                <!-- Right: refresh button -->
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-btn color="primary" variant="tonal" rounded="pill" prepend-icon="mdi-refresh"
                        :loading="isLoading" @click="getData">
                        Actualiser
                    </v-btn>
                </div>
            </div>
        </div>

        <v-divider />

        <!-- Scrollable content -->
        <div style="flex: 1 1 auto; overflow-x: auto; overflow-y: auto;">

            <!-- Loading skeleton -->
            <div v-if="isLoading" class="pa-6 d-flex justify-center">
                <v-progress-circular indeterminate color="primary" />
            </div>

            <!-- Empty state -->
            <div v-else-if="grouped.length === 0"
                class="d-flex flex-column align-center justify-center text-medium-emphasis"
                style="height: 200px; gap: 8px;">
                <v-icon icon="mdi-package-variant" size="40" />
                <span class="text-body-2">Aucun item en inventaire</span>
            </div>

            <!-- Grouped content -->
            <v-expansion-panels v-else variant="accordion" class="ma-4" style="gap: 8px;">
                <v-expansion-panel
                    v-for="fournisseur in grouped"
                    :key="fournisseur.fournisseurId"
                    rounded="lg"
                    elevation="1">

                    <!-- Fournisseur header -->
                    <v-expansion-panel-title>
                        <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                            <v-avatar color="primary" variant="tonal" size="32" rounded="md">
                                <v-icon icon="mdi-account-group-outline" size="18" />
                            </v-avatar>
                            <span class="text-subtitle-2 font-weight-bold">
                                {{ getFournisseurLettresDappel(fournisseur.fournisseurId) }}
                            </span>
                            <v-chip size="x-small" color="primary" variant="tonal">
                                {{ fournisseur.semis.length }} semi<span v-if="fournisseur.semis.length > 1">s</span>
                            </v-chip>
                            <v-chip size="x-small" color="secondary" variant="tonal"
                                prepend-icon="mdi-layers-outline">
                                {{ fournisseur.totalQuantité }} unités
                            </v-chip>
                            <v-chip v-if="fournisseur.quarantaineCount > 0" size="x-small" color="warning"
                                variant="tonal" prepend-icon="mdi-lock-outline">
                                {{ fournisseur.quarantaineCount }} Q
                            </v-chip>
                        </div>
                    </v-expansion-panel-title>

                    <v-expansion-panel-text class="pa-0">
                        <!-- Semi groups -->
                        <div v-for="semi in fournisseur.semis" :key="semi.semiId" class="semi-group">

                            <!-- Semi header -->
                            <div class="semi-header px-4 py-2" style="gap: 6px;">
                                <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                                    <v-icon icon="mdi-sprout-outline" size="16" color="success" />
                                    <span class="text-body-2 font-weight-bold">Semi {{ semi.semiId }}</span>
                                    <v-chip size="x-small" color="success" variant="tonal"
                                        prepend-icon="mdi-layers-outline">
                                        {{ semi.totalQuantité }} unités
                                    </v-chip>
                                    <v-chip v-if="semi.quarantaineCount > 0" size="x-small" color="warning"
                                        variant="tonal" prepend-icon="mdi-lock-outline">
                                        {{ semi.quarantaineCount }} Q
                                    </v-chip>
                                    <v-spacer />
                                    <template v-if="!smAndDown">
                                        <v-btn v-if="palettesQ(semi.récoltes.flatMap(r => r.items)).length === semi.récoltes.flatMap(r => r.items).length"
                                            size="x-small" color="success" variant="tonal"
                                            rounded="pill" prepend-icon="mdi-lock-open-outline"
                                            @click="relacherPalettes(palettesQ(semi.récoltes.flatMap(r => r.items)))">
                                            Relâcher tout le semi
                                        </v-btn>
                                        <v-btn v-else
                                            size="x-small" color="warning" variant="tonal"
                                            rounded="pill" prepend-icon="mdi-lock-outline"
                                            @click="mettrEnQuarantaine(palettesV(semi.récoltes.flatMap(r => r.items)))">
                                            Mettre en quarantaine
                                        </v-btn>
                                    </template>
                                </div>
                                <div v-if="smAndDown" class="mt-2">
                                    <v-btn v-if="palettesQ(semi.récoltes.flatMap(r => r.items)).length === semi.récoltes.flatMap(r => r.items).length"
                                        size="small" color="success" variant="tonal" rounded="pill"
                                        prepend-icon="mdi-lock-open-outline" block
                                        @click="relacherPalettes(palettesQ(semi.récoltes.flatMap(r => r.items)))">
                                        Relâcher tout le semi
                                    </v-btn>
                                    <v-btn v-else
                                        size="small" color="warning" variant="tonal" rounded="pill"
                                        prepend-icon="mdi-lock-outline" block
                                        @click="mettrEnQuarantaine(palettesV(semi.récoltes.flatMap(r => r.items)))">
                                        Mettre en quarantaine
                                    </v-btn>
                                </div>
                            </div>

                            <!-- Récolte date groups -->
                            <div v-for="récolte in semi.récoltes" :key="récolte.dateRécolte">

                                <!-- Date subheader -->
                                <div class="date-header px-4 py-2" style="gap: 6px;">
                                    <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                                        <v-icon icon="mdi-calendar-outline" size="14"
                                            class="text-medium-emphasis" />
                                        <span class="text-caption text-medium-emphasis">
                                            Récolte : {{ récolte.dateRécolte }}
                                        </span>
                                        <span class="text-caption text-medium-emphasis">
                                            · {{ récolte.items.length }} palette<span
                                                v-if="récolte.items.length > 1">s</span>
                                            · {{ récolte.totalQuantité }} unités
                                        </span>
                                        <v-spacer />
                                        <template v-if="!smAndDown">
                                            <v-btn v-if="palettesQ(récolte.items).length === récolte.items.length"
                                                size="x-small" color="success" variant="tonal"
                                                rounded="pill" prepend-icon="mdi-lock-open-outline"
                                                @click="relacherPalettes(palettesQ(récolte.items))">
                                                Relâcher la récolte
                                            </v-btn>
                                            <v-btn v-else
                                                size="x-small" color="warning" variant="tonal"
                                                rounded="pill" prepend-icon="mdi-lock-outline"
                                                @click="mettrEnQuarantaine(palettesV(récolte.items))">
                                                Mettre en quarantaine
                                            </v-btn>
                                        </template>
                                    </div>
                                    <div v-if="smAndDown" class="mt-2">
                                        <v-btn v-if="palettesQ(récolte.items).length === récolte.items.length"
                                            size="small" color="success" variant="tonal" rounded="pill"
                                            prepend-icon="mdi-lock-open-outline" block
                                            @click="relacherPalettes(palettesQ(récolte.items))">
                                            Relâcher la récolte
                                        </v-btn>
                                        <v-btn v-else
                                            size="small" color="warning" variant="tonal" rounded="pill"
                                            prepend-icon="mdi-lock-outline" block
                                            @click="mettrEnQuarantaine(palettesV(récolte.items))">
                                            Mettre en quarantaine
                                        </v-btn>
                                    </div>
                                </div>

                                <!-- Items: table on desktop, cards on mobile -->
                                <v-table v-if="!smAndDown" density="compact" class="mx-4 mb-2 items-table"
                                    rounded="lg">
                                    <thead>
                                        <tr>
                                            <th class="text-left">Palette ID</th>
                                            <th class="text-left">État</th>
                                            <th class="text-left">Réception</th>
                                            <th class="text-right">Quantité</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="item in récolte.items" :key="item.paletteId">
                                            <td class="text-body-2 font-weight-medium">
                                                {{ item.paletteId }}
                                            </td>
                                            <td>
                                                <v-chip v-if="pendingIds.has(item.paletteId)" color="info" variant="tonal"
                                                    size="x-small" style="cursor:pointer"
                                                    @click="promptAnnulerRequete(item.paletteId)">
                                                    <template #prepend>
                                                        <v-progress-circular indeterminate size="10" width="2" class="mr-1" />
                                                    </template>
                                                    en cours
                                                </v-chip>
                                                <v-chip v-else :color="étatColor(item.état)" variant="tonal"
                                                    size="x-small" :prepend-icon="étatIcon(item.état)">
                                                    {{ formatÉtat(item.état) }}
                                                </v-chip>
                                            </td>
                                            <td class="text-body-2 text-medium-emphasis">
                                                {{ formatRéception(item.dateRéception) }}
                                            </td>
                                            <td class="text-right text-body-2">{{ item.quantité }}</td>
                                            <td class="text-right">
                                                <v-btn v-if="item.état === 'Q'" size="x-small" color="success" variant="text"
                                                    rounded="pill" prepend-icon="mdi-lock-open-outline"
                                                    :disabled="pendingIds.has(item.paletteId)"
                                                    @click="relacherPalettes([item.paletteId])">
                                                    Relâcher
                                                </v-btn>
                                                <v-btn v-else size="x-small" color="warning" variant="text"
                                                    rounded="pill" prepend-icon="mdi-lock-outline"
                                                    :disabled="pendingIds.has(item.paletteId)"
                                                    @click="mettrEnQuarantaine([item.paletteId])">
                                                    Mettre en quarantaine
                                                </v-btn>
                                            </td>
                                        </tr>
                                    </tbody>
                                </v-table>

                                <!-- Mobile cards -->
                                <div v-else class="px-3 pb-3" style="display: flex; flex-direction: column; gap: 8px;">
                                    <v-card v-for="item in récolte.items" :key="item.paletteId"
                                        variant="outlined" rounded="lg" class="palette-card pa-3">
                                        <div class="d-flex align-center justify-space-between mb-1">
                                            <span class="text-body-2 font-weight-bold">{{ item.paletteId }}</span>
                                            <v-chip v-if="pendingIds.has(item.paletteId)" color="info" variant="tonal"
                                                size="x-small" style="cursor:pointer"
                                                @click="promptAnnulerRequete(item.paletteId)">
                                                <template #prepend>
                                                    <v-progress-circular indeterminate size="10" width="2" class="mr-1" />
                                                </template>
                                                en cours
                                            </v-chip>
                                            <v-chip v-else :color="étatColor(item.état)" variant="tonal"
                                                size="x-small" :prepend-icon="étatIcon(item.état)">
                                                {{ formatÉtat(item.état) }}
                                            </v-chip>
                                        </div>
                                        <div class="d-flex align-center justify-space-between">
                                            <div style="display: flex; flex-direction: column; gap: 2px;">
                                                <span class="text-caption text-medium-emphasis">
                                                    {{ formatRéception(item.dateRéception) }}
                                                </span>
                                                <span class="text-caption">
                                                    Quantité : <strong>{{ item.quantité }}</strong>
                                                </span>
                                            </div>
                                            <v-btn v-if="item.état === 'Q'" size="x-small" color="success" variant="tonal"
                                                rounded="pill" prepend-icon="mdi-lock-open-outline"
                                                :disabled="pendingIds.has(item.paletteId)"
                                                @click="relacherPalettes([item.paletteId])">
                                                Relâcher
                                            </v-btn>
                                            <v-btn v-else size="x-small" color="warning" variant="tonal"
                                                rounded="pill" prepend-icon="mdi-lock-outline"
                                                :disabled="pendingIds.has(item.paletteId)"
                                                @click="mettrEnQuarantaine([item.paletteId])">
                                                Mettre en quarantaine
                                            </v-btn>
                                        </div>
                                    </v-card>
                                </div>

                            </div>
                        </div>
                    </v-expansion-panel-text>

                </v-expansion-panel>
            </v-expansion-panels>

        </div>

    </v-card>

    <!-- Confirm annulation dialog -->
    <v-dialog v-model="confirmDialog" max-width="400" persistent>
        <v-card rounded="xl">
            <v-card-title class="text-subtitle-1 font-weight-bold pt-5 px-5">
                Annuler la requête ?
            </v-card-title>
            <v-card-text class="px-5 text-body-2 text-medium-emphasis">
                La requête en attente pour la palette
                <strong>{{ confirmPaletteId }}</strong> sera supprimée.
                Cette action est irréversible.
            </v-card-text>
            <v-card-actions class="px-5 pb-4">
                <v-spacer />
                <v-btn variant="text" @click="confirmDialog = false">Annuler</v-btn>
                <v-btn color="error" variant="tonal" rounded="pill" @click="confirmerAnnulation">
                    Supprimer
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* ── Semi group ───────────────────────────── */
.semi-group {
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.semi-header {
    background: rgba(var(--v-theme-surface-variant), 0.12);
}

/* ── Date subheader ───────────────────────── */
.date-header {
    background: rgba(var(--v-theme-surface-variant), 0.06);
}

/* ── Items table ──────────────────────────── */
.items-table {
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
